import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getActiveCargoes } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { CargoTempCategory } from '../../types/ViewModel/CargoTempLog/CargoInTankItem';
import { CargoTemperatureConfig } from '../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { CargoTemperatureLogEntry } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLog';
import { CargoTemperatureLogDate } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { NoonReportSectionData } from '../../types/ViewModel/NoonReportSectionData';
import { Tank } from '../../types/ViewModel/Vessel/Tank';
import sortUtils from '../../utils/sortUtils';
import { CargoInfo } from 'src/types/ViewModel/CargoTempLog/CargoInfo';

export const MAX_OXYGEN_PER_CENT_VALUE = 22;
export const MAX_OXYGEN_PPM_VALUE = 220000;
export const MAX_TEMPERATURE_VALUE = 100;
export const MAX_PRESSURE_VALUE = 601;

export enum SortColumnEnum {
    CargoNr,
    TradeName,
    Tank,
    Category,
}

export enum tabEnum {
    reporting = 1,
    config = 2,
    export = 3,
}

export const inputTypes = ['temp', 'o2', 'pressure', 'comment'];

export type TankDetails = {
    tank: Tank;
    cargoLogs: CargoTemperatureLogEntry[][];
    cargoNumber: string;
    tradeName: string;
    category: CargoTempCategory;
    productId?: number;
    chartererName?: string;
};

export type CargoTemperatureControlVM = {
    cargoTanks: TankDetails[];
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

export const getComparison = (sortDescending: boolean, stringA: string, stringB: string, tankA: string, tankB: string): number => {
    const { compareTankNames } = sortUtils();
    if (stringA > stringB) return sortDescending ? -1 : 1;
    if (stringA < stringB) return sortDescending ? 1 : -1;
    return compareTankNames(tankA, tankB, sortDescending);
};

const isCargoTemperatureLogInSlot = (logItem: CargoTemperatureLogEntry, cargoNumber: string, tankId: number, logTime: string): boolean =>
    logItem.cargoNumber === cargoNumber && logItem.tankId === tankId && formatLogTime(logItem.loggedTimeLocalTime) === logTime;

const getConfiguredFieldIds = (config: CargoTemperatureConfig): number[] =>
    config.inputFields
        .map((inputField) => inputField.cargoTemperatureInputFieldId)
        .filter((fieldId, index, fieldIds) => fieldIds.indexOf(fieldId) === index)
        .sort((fieldIdA, fieldIdB) => fieldIdA - fieldIdB);

const getOnBoardLogEntryForSlot = (
    config: CargoTemperatureConfig,
    cargoTempLog: CargoTemperatureLogEntry[],
    tankId: number,
    logTime: string
): CargoTemperatureLogEntry | undefined => {
    const fieldOneLogEntry = cargoTempLog.find(
        (logItem) => isCargoTemperatureLogInSlot(logItem, config.cargoNumber, tankId, logTime) && logItem.cargoTemperatureFieldId === 1
    );
    if (fieldOneLogEntry) return fieldOneLogEntry;

    return getConfiguredFieldIds(config)
        .map((fieldId) =>
            cargoTempLog.find(
                (logItem) =>
                    isCargoTemperatureLogInSlot(logItem, config.cargoNumber, tankId, logTime) && logItem.cargoTemperatureFieldId === fieldId
            )
        )
        .find((logItem): logItem is CargoTemperatureLogEntry => logItem !== undefined);
};

export const validateCargoTempLog = (
    cargoTempLogDate: CargoTemperatureLogDate,
    cargoTempLog: CargoTemperatureLogEntry[],
    configs: CargoTemperatureConfig[]
): boolean => {
    if (!cargoTempLogDate || !configs || !cargoTempLog) return false;

    return configs.every((conf) => {
        const tankIds = cargoTempLogDate.cargoInTankList.filter((cargoInTank) => cargoInTank.cargoNumber === conf.cargoNumber).map((c) => c.tankId);
        return tankIds.every((tankId) => {
            return conf.logTimes.every((logTime) => {
                const onBoardItem = getOnBoardLogEntryForSlot(conf, cargoTempLog, tankId, logTime);
                if (onBoardItem && !onBoardItem.isOnBoard) return true;
                return conf.inputFields.every((input) => {
                    const logEntry = cargoTempLog.find(
                        (logItem) =>
                            isCargoTemperatureLogInSlot(logItem, conf.cargoNumber, tankId, logTime) &&
                            logItem.cargoTemperatureFieldId === input.cargoTemperatureInputFieldId
                    );
                    return logEntry?.value !== null && logEntry?.value !== undefined;
                });
            });
        });
    });
};

export const synchronizeCargoTempLogIsOnBoard = (
    changedLog: CargoTemperatureLogEntry,
    cargoTempLog: CargoTemperatureLogEntry[],
    configs: CargoTemperatureConfig[]
): void => {
    const config = configs.find((candidate) => candidate.cargoNumber === changedLog.cargoNumber);
    if (!config) return;

    const changedLogTime = formatLogTime(changedLog.loggedTimeLocalTime);
    cargoTempLog
        .filter(
            (logItem) =>
                isCargoTemperatureLogInSlot(logItem, changedLog.cargoNumber, changedLog.tankId, changedLogTime)
        )
        .forEach((logItem) => {
            logItem.isOnBoard = changedLog.isOnBoard;
        });
};

export const getCategoryName = (category: CargoTempCategory | undefined): string | null => {
    switch (category) {
        case CargoTempCategory.Inhibited:
            return 'Inhibited';
        case CargoTempCategory.HeatSensitive:
            return 'Heat sensitive';
        case CargoTempCategory.Cooled:
            return 'Cooled';
        case CargoTempCategory.Heated:
            return 'Heated';
        case CargoTempCategory.NonHeat:
            return 'Non-heat';
        default:
            return null;
    }
};

export const formatLogTime = (date: Date): string => {
    return ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2);
};

// Normalise log time strings (e.g. "8:00", "08:00", "08:00:00") to canonical "HH:mm"
// so dedupe operates on equivalent values regardless of formatting drift.
export const normaliseLogTime = (raw: string): string | null => {
    if (!raw || typeof raw !== 'string') return null;
    const match = raw.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const hours = Math.min(23, parseInt(match[1], 10));
    const minutes = Math.min(59, parseInt(match[2], 10));
    return `${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}`;
};

// Dedupe a list of "HH:mm" log times, normalising entries first.
// Returns a sorted, unique list — never mutates the input.
export const dedupeLogTimes = (logTimes: ReadonlyArray<string> | null | undefined): string[] => {
    if (!logTimes || logTimes.length === 0) return [];
    const seen = new Set<string>();
    for (const raw of logTimes) {
        const normalised = normaliseLogTime(raw);
        if (normalised) seen.add(normalised);
    }
    return Array.from(seen).sort();
};

// Build the natural key used to identify a single cargo temperature reading.
// Used to dedupe both the rendered cells and the save payload so we never
// send (or render) more than one row per (cargoNumber, tankId, fieldId, time).
export const getCargoTemperatureLogNaturalKey = (entry: {
    cargoNumber: string;
    tankId: number;
    cargoTemperatureFieldId: number;
    loggedTimeLocalTime: Date | string | null | undefined;
}): string => {
    const time = entry.loggedTimeLocalTime instanceof Date ? entry.loggedTimeLocalTime : entry.loggedTimeLocalTime ? new Date(entry.loggedTimeLocalTime) : null;
    const timeKey = time && !isNaN(time.getTime()) ? `${time.getFullYear()}-${time.getMonth()}-${time.getDate()}T${formatLogTime(time)}` : 'null';
    return `${entry.cargoNumber}|${entry.tankId}|${entry.cargoTemperatureFieldId}|${timeKey}`;
};

// Score a CargoTemperatureLog row so the best (most informative) duplicate wins.
// Higher score wins. Used for both UI cell selection and save payload dedupe.
const scoreLogEntry = (entry: CargoTemperatureLogEntry): number => {
    let score = 0;
    if (entry.value !== null && entry.value !== undefined) score += 1000;
    if (entry.comment && entry.comment.length > 0) score += 100;
    if (entry.isOnBoard === false) score += 10; // explicit "not on board" carries info
    if (entry.cargoTemperatureLogId && entry.cargoTemperatureLogId > 0) score += 1;
    return score;
};

// Pick the best CargoTemperatureLog row from a set of duplicates by score.
// Tie-breaks on highest cargoTemperatureLogId so the most recently saved row wins.
export const pickBestLogEntry = (candidates: CargoTemperatureLogEntry[]): CargoTemperatureLogEntry | undefined => {
    if (!candidates || candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0];
    return [...candidates].sort((a, b) => {
        const scoreDiff = scoreLogEntry(b) - scoreLogEntry(a);
        if (scoreDiff !== 0) return scoreDiff;
        return (b.cargoTemperatureLogId ?? 0) - (a.cargoTemperatureLogId ?? 0);
    })[0];
};

// Dedupe a CargoTemperatureLog payload by natural key, keeping the best row
// (non-null value > non-empty comment > explicit isOnBoard=false > highest id).
export const dedupeCargoTemperatureLog = (
    entries: ReadonlyArray<CargoTemperatureLogEntry> | null | undefined
): CargoTemperatureLogEntry[] => {
    if (!entries || entries.length === 0) return [];
    const groups = new Map<string, CargoTemperatureLogEntry[]>();
    for (const entry of entries) {
        const key = getCargoTemperatureLogNaturalKey(entry);
        const list = groups.get(key);
        if (list) list.push(entry);
        else groups.set(key, [entry]);
    }
    const result: CargoTemperatureLogEntry[] = [];
    groups.forEach((list) => {
        const best = pickBestLogEntry(list);
        if (best) result.push(best);
    });
    return result;
};

export const CreateCargoDataForExcelTemperatureReport = (
    cargoNumbers: string[],
    activeMasterPlan: StowagePlan,
    cargoGuids?: string[]
): CargoInfo[] => {
    const cargoData: CargoInfo[] = [];
    const activeCargoes = getActiveCargoes(activeMasterPlan);
    cargoNumbers.forEach((cargoNumber, index) => {
        const voyCargoNr = cargoNumber.split('-');
        const voyageNumber = voyCargoNr[0];
        if (voyCargoNr.length < 2) return; // skip if cargo number format is invalid
        // try to parse the cargo sequence number, default to -1 if not found
        const cargoSeqNumber = parseInt(voyCargoNr[1]) || -1;

        // Use cargoGuid when available to resolve the correct cargo when duplicate cargo numbers exist in a voyage
        const cargoGuid = cargoGuids?.[index];
        const cargo = cargoGuid
            ? activeCargoes.find((c) => c.cargoGuid === cargoGuid)
            : activeCargoes.find((c) => c.voyageNumber === voyageNumber && c.cargoNumber === cargoSeqNumber);

        // Always use voyage+cargoNumber for matching cargo operations — cargoGuid is optional on
        // RotationCargoOperation and may not be populated. Phantom/manual cargoes have no operations,
        // so this approach safely returns only the real (IMOS) cargo's operations.
        const cargoOperations = activeMasterPlan.cargoOperations.filter(
            (co) => co.cargo.voyageNumber === voyageNumber && co.cargo.cargoNumber === cargoSeqNumber
        );
        const stowages = activeMasterPlan.stowageItems.filter((s) => cargoOperations.some((co) => co.cargoOperationGuid === s.cargoOperationGuid));
        if (stowages.length === 0) return;
        if (cargo) {
            // deduplicate tanks list
            const tanks = Array.from(new Set(stowages.map((s) => s.tankId)));
            cargoData.push({
                cargoNumber: cargoNumber,
                loadingPortName: cargo.loadingPortNames[0]?.name || '',
                unloadingPortName: cargo.unloadingPortNames[0]?.name || '',
                tanks: tanks,
                adjacentTempControlRequired: cargo.product?.maxAdjacentTemperature != null,
            });
        }
    });

    return cargoData;
};
